/**
 * COM-9.13 — the two setup steps that fail by producing nothing.
 *
 * Mapping a pipeline stage to a milestone, and declaring what the system size
 * column is counted in, are both optional as far as the schema is concerned and
 * both silently disable everything downstream. An account with commission switched
 * on and neither of them done reports no earnings, no gaps and a clean bill of
 * health, and every one of those statements is technically true.
 *
 * ⚠️ THIS IS COM-8.9's `not_measured` PROBLEM WITH DIFFERENT NOUNS, so it reuses
 * that shape rather than inventing a second vocabulary for the same idea. See
 * `holdAlertState` in `commissionOpsHealth.ts`: a state that is not a count,
 * because the count is zero in both the healthy case and the unconfigured one.
 *
 * ⚠️ AND THE SENTENCE NAMES WHAT THE SILENCE IS COSTING, for the same reason
 * `describeHoldAlert` does. "No stage is mapped" invites "so there is nothing to
 * map it for". "13 deals have people on them and none of them can release a
 * payment" is the true statement and the one that gets acted on.
 *
 * Measured against the live database on 2026-08-28, before this was written:
 *   * 8 of 1,081 stages carry a mapping, all 8 in one account
 *   * 3 accounts have commission on; 2 of them have no mapping at all
 *   * 1 account in the database has declared the size unit
 *   * 0 components anywhere are per_watt or percent_of_net_margin
 */

// ─────────────────────────────────────────────────────────────────────────────
// Shapes — these mirror commission_account_readiness() exactly
// ─────────────────────────────────────────────────────────────────────────────

export type ReadinessCheckKey = 'stage_milestone_mapping' | 'system_size_unit';

/**
 * ⚠️ `'not_needed'` IS NOT `'configured'` AND IS NOT A SOFTENING OF
 * `'not_configured'`. Only per-watt and net-margin components multiply by system
 * size. An account paying flat amounts and percentages of contract never reads
 * that column, and nagging it would be a false alarm of exactly the kind that
 * teaches people to stop reading this card.
 */
export type ReadinessState = 'not_configured' | 'configured' | 'not_needed';

export interface AccountReadinessCheck {
  checkKey: ReadinessCheckKey;
  state: ReadinessState;
  /** What the state is about. Its meaning depends on the check and the state. */
  affected: number;
}

export type ReadinessTone = 'gap' | 'ok' | 'idle';

// ─────────────────────────────────────────────────────────────────────────────
// Copy
// ─────────────────────────────────────────────────────────────────────────────

export const READINESS_COPY = {
  title: 'Account setup',
  description:
    'Two steps that fail by producing nothing rather than by producing an error. Neither of them reports a problem when it is skipped, which is why they are reported here.',

  mappingTitle: 'Stages mapped to milestones',
  unitTitle: 'System size unit',

  /** ⚠️ The whole point of the card, said once. */
  quietAccount:
    'An account with neither of these done reports no earnings and no gaps, and both of those statements are true. Quiet is not the same as healthy.',

  mappingExplanation:
    'Commission is released when a project reaches a milestone, and a project reaches one by entering a stage mapped to it. With no mapping, nothing can ever be released.',
  unitExplanation:
    'Per-watt and net-margin components multiply by system size, and the column holds both kW and watts. Until somebody says which, 8 could mean 8 watts or 8,000.',

  unitRequiredOnSave:
    'Declaring the unit is required the moment a component is paid on system size, and not before. That is the only point in the setup where the answer is knowable.',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Reading the state
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ `not_needed` IS NOT A PROBLEM AND NOT A TICK. It is genuinely neither, so it
 * gets its own tone rather than being folded into one of the other two.
 */
export function readinessTone(state: ReadinessState): ReadinessTone {
  switch (state) {
    case 'not_configured':
      return 'gap';
    case 'configured':
      return 'ok';
    case 'not_needed':
      return 'idle';
  }
}

export function readinessTitle(checkKey: ReadinessCheckKey): string {
  return checkKey === 'stage_milestone_mapping' ? READINESS_COPY.mappingTitle : READINESS_COPY.unitTitle;
}

export function readinessExplanation(checkKey: ReadinessCheckKey): string {
  return checkKey === 'stage_milestone_mapping'
    ? READINESS_COPY.mappingExplanation
    : READINESS_COPY.unitExplanation;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The sentence for each state.
 *
 * ⚠️ THE ZERO CASE IS SEPARATE ON PURPOSE. Two of the three accounts with
 * commission switched on have no mapping AND no contributors, so the
 * consequence-naming sentence would read "0 deals have people on them and none of
 * them can release a payment", which is both true and useless. Same split as
 * `holdsNotMeasuredNoneHeld`.
 */
export function describeReadiness(check: AccountReadinessCheck): string {
  if (check.checkKey === 'stage_milestone_mapping') {
    switch (check.state) {
      case 'not_configured':
        return check.affected === 0
          ? 'No stage in this account is mapped to a milestone, so nothing can ever be released. Nobody is on a deal yet either, so nothing is stuck today.'
          : `${plural(check.affected, 'deal has', 'deals have')} people on them and none of them can release a payment. No stage in this account is mapped to a milestone.`;
      case 'configured':
        return `${plural(check.affected, 'stage is', 'stages are')} mapped to a milestone.`;
      case 'not_needed':
        // Unreachable: the mapping check never answers not_needed. Kept total so
        // adding a state to the database cannot silently render nothing here.
        return 'This check does not apply to this account.';
    }
  }

  switch (check.state) {
    case 'not_configured':
      return `${plural(check.affected, 'component is', 'components are')} paid on system size, and nobody has said whether this account measures it in kW or in watts. Those components cannot produce a figure until somebody does.`;
    case 'configured':
      return `${plural(check.affected, 'component is', 'components are')} paid on system size, and the unit is declared.`;
    case 'not_needed':
      return 'No component here is paid on system size, so the unit is not needed yet. It becomes required the moment one is.';
  }
}

/**
 * How many checks are actually unconfigured.
 *
 * ⚠️ COUNTS `not_configured` ONLY. Folding `not_needed` in would make a correctly
 * configured flat-rate account report a permanent outstanding problem it can never
 * clear, which is the fastest way to teach somebody to ignore this card.
 */
export function readinessGapCount(checks: readonly AccountReadinessCheck[]): number {
  return checks.filter((c) => c.state === 'not_configured').length;
}

/**
 * The card's headline.
 *
 * ⚠️ RETURNS null WHEN THERE IS NOTHING TO SAY, rather than "all clear". The
 * caller decides whether it was allowed to ask in the first place; a reassuring
 * sentence rendered from an unauthorised or failed read is the failure this whole
 * story is about.
 */
export function describeReadinessHeadline(checks: readonly AccountReadinessCheck[]): string | null {
  if (checks.length === 0) return null;
  const gaps = readinessGapCount(checks);
  if (gaps > 0) {
    return `${plural(gaps, 'setup step is', 'setup steps are')} not done, and neither of them reports a problem anywhere else.`;
  }

  // ⚠️ `not_needed` IS NOT `done`, AND THIS SENTENCE CLAIMED IT WAS. Found by
  // looking at the page: the sandbox has no size-derived component, so the unit
  // check reads `not_needed` and the headline read "Both setup steps are done for
  // this account". Nobody had done that step. It simply did not apply yet, and a
  // headline that calls it done is the same false reassurance as the zero this
  // whole card exists to correct. It read fine in the source.
  const notNeeded = checks.filter((c) => c.state === 'not_needed').length;
  if (notNeeded === 0) return 'Both setup steps are done for this account.';
  return `Nothing is outstanding. ${plural(notNeeded, 'step does', 'steps do')} not apply to this account yet, and will say so when it does.`;
}
